import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { theme } from "../../theme.js";
import { HeroEditable } from "../../components/heroSection.jsx";
import StepIndicator from "../../components/base/StepIndicator.jsx";
import CreateRaceOptions from "./raceOptions.jsx";
import CreateRacePrices from "./racePrices.jsx";
import CreateRaceGeneralInfos from "./raceInfos.jsx";
import Button from "../../components/base/Button.jsx";
import { getOrgIdFromStorage } from "../../utils/storage.js";
import { DEFAULT_IMAGE, API_URL } from "../../utils/constants.js";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import LoadingOverlay from "../../components/LoadingOverlay.jsx";

// Helper function to ensure all options have a 'key' property
const normalizeOptions = (optionsList) => {
  return optionsList.map((opt) => ({
    ...opt,
    key: opt.key || opt.id_bdd?.toString() || opt.id?.toString() || Math.random().toString(36).substr(2, 9),
  }));
};

export default function CreateRace() {
  const navigate = useNavigate();
  const { event_id } = useParams();
  const loc = useLocation();
  const editRace = loc.state?.editRace;
  const navOrgId = loc.state?.orgId;
  const orgId = navOrgId || getOrgIdFromStorage();

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [selectedSportExtra, setSelectedSportExtra] = useState({});

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [extraInfo, setExtraInfo] = useState({  });
  const [gpxTrackId, setGpxTrackId] = useState(null);

  // Step 1: General Information
  const [raceInfo, setRaceInfo] = useState({
    title: "New Race",
    sport: "",
    extra_info: {},
    description: "",
    image: DEFAULT_IMAGE,
    date: "",
    capacity: "",
    gpx: null,
  });

  // Step 2: Options & Requirements
  const [options, setOptions] = useState(() => normalizeOptions([
    { id: 1, title: "tshirt", name: "T-shirt avec taille",required: true, price: 0, choices:["S", "M", "L", "XL"], type: "enum" },
    { id: 2, title: "lunch", name: "Repas (végétarien ou non)", required: false, price: 15, choices:["Omnivore", "Végétarien", "Végan"], type: "enum" },
    { id: 3, title: "photo", name: "Pack photo",required : false, price: 35, choices: [], type: "boolean" },
    { id: 4, title: "shuttle", name: "Navette",required: false, price: 20, choices: [], type: "boolean" },
  ]));

  const [requirements, setRequirements] = useState({
    licenseChoice: { name: "pps_or_license", label: "PPS ou Licence", value: "PPS ou Licence" },
    documents: [
      { id: 1, type: "medical_cert", label: "Certificat médical", level: "optional" },
      { id: 2, type: "parental_auth", label: "Autorisation parentale", level: "optional" },
    ],
  });

  // Step 3: Prices
  const [prices, setPrices] = useState([]);
  const [corrals, setCorrals] = useState(["1H", "1H15", "1H30"]);

  const handleRaceInfoChange = (e) => {
  const { name, value } = e.target;
  setRaceInfo((prev) => ({ ...prev, [name]: value }));
  };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setRaceInfo((prev) => ({ ...prev, image: reader.result }));
      };
      console.log('Selected image file:', raceInfo.image);
      reader.readAsDataURL(file);
    }
  };

  // Pre-fill form if editing a race
  useEffect(() => {
    if (editRace) {
      const formatDateString = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        // Format as yyyy-MM-ddThh:mm:ss for datetime-local input
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      setRaceInfo({
        title: editRace.name || "New Race",
        sport: editRace.sport_id || "",
        extra_info: editRace.extra_info || {},
        description: editRace.description || "",
        image: editRace.image || DEFAULT_IMAGE,
        date: formatDateString(editRace.date),
        capacity: editRace.capacity || "",
        gpx: editRace.gpx || null,
      });

      // Set GPX track ID if it exists
      if (editRace.gpx) {
        setGpxTrackId(editRace.gpx);
      }

      // Pre-fill extra info
      setExtraInfo(editRace.extra_info || {});

      // Pre-fill options if they exist
      if (editRace.options && Array.isArray(editRace.options)) {
        setOptions(normalizeOptions(editRace.options));
      }

      // Pre-fill requirements if they exist
      if (editRace.requirements && Array.isArray(editRace.requirements)) {
        const transformedRequirements = {
          licenseChoice: { name: "none", label: "Aucun", value: "none" },
          documents: []
        };
        
        editRace.requirements.forEach(req => {
          if (req.type === "license") {
            // Map documents back to the appropriate option
            let optionName = "none";
            let joinedDocs = req.documents && req.documents.length > 0 ? req.documents.join(" ou ") : "none";
            
            if (joinedDocs === "PPS") {
              optionName = "pps";
            } else if (joinedDocs === "Licence") {
              optionName = "license";
            } else if (joinedDocs === "PPS ou Licence" || joinedDocs === "Licence ou PPS") {
              optionName = "pps_or_license";
            }
            
            const licenseOptions = [
              { name: "pps", label: "PPS seulement", value: "PPS" },
              { name: "license", label: "Licence seulement", value: "Licence" },
              { name: "pps_or_license", label: "PPS ou Licence", value: "PPS ou Licence" },
              { name: "none", label: "Aucun", value: "none" },
            ];
            
            const selectedOption = licenseOptions.find(opt => opt.name === optionName) || licenseOptions[3];
            transformedRequirements.licenseChoice = selectedOption;
          } else {
            // Add as a document
            transformedRequirements.documents.push({
              id: req.id_bdd,
              type: req.type,
              label: req.name,
              level: req.min_required === 1 ? "required" : "optional"
            });
          }
        });
        
        setRequirements(transformedRequirements);
      }

      // Pre-fill prices if they exist
      if (editRace.prices && Array.isArray(editRace.prices)) {
        setPrices(editRace.prices);
      }

      // Pre-fill corrals if they exist
      if (editRace.corrals && Array.isArray(editRace.corrals)) {
        setCorrals(editRace.corrals);
      }
    } else if (event_id) {
      // Fetch event image for new race
      fetch(`${API_URL}/api/events?event_id=${event_id}`)
        .then(res => res.json())
        .then(data => {
          if (data.image) {
            setRaceInfo(prev => ({
              ...prev,
              image: data.image
            }));
          }
        })
        .catch(err => console.error("Failed to fetch event image:", err));
    }
  }, [editRace, event_id]);

  const steps = [
    { number: 1, label: "Informations générales" },
    { number: 2, label: "Options & exigences" },
    { number: 3, label: "Tarifs" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const payload = {
      name: raceInfo.title,
      description: raceInfo.description,
      sport: raceInfo.sport,
      extra_info: extraInfo,
      date: raceInfo.date,
      capacity: parseInt(raceInfo.capacity) || 0,
      elev_gain: parseFloat(raceInfo.elev_gain) || 0,
      image: raceInfo.image,
      gpx: gpxTrackId,
      event: parseInt(event_id),
      options: options,
      requirements: requirements,
      prices: prices,
      corrals: corrals,
    };
    try {
      const endpoint = editRace 
        ? `${API_URL}/api/races/${editRace.id_bdd}` 
        : `${API_URL}/api/races`;
      const method = editRace ? "PUT" : "POST";

      const res = await fetch(endpoint, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
          const data = await res.json();
          navigate(`/events/${event_id}`, { state: { refresh: true } });
      } else {
        setError(editRace ? "Erreur lors de la modification de la course" : "Erreur lors de la création de la course");
      }
    } catch (err) {
      console.error("Error creating/modifying race:", err);
      setError("Erreur de connexion au serveur");
    } finally {
      setSubmitting(false);
    }
  };

   
  function isStep1Valid() {
    // Required: title, sport, date, description
    if (!raceInfo.title || !raceInfo.sport || !raceInfo.date || !raceInfo.description) return false;
    // Required dynamic fields
    if (Object.keys(selectedSportExtra || {}).length > 0) {
      for (const key of Object.keys(selectedSportExtra)) {
        if (!extraInfo[key] && extraInfo[key] !== 0) return false;
      }
    }
    return true;
  }



  if (loading) {
    return <LoadingOverlay isVisible={true} message={editRace ? "Updating event..." : "Creating event..."} />;
  }



  return (
    <div style={{ ...styles.container, padding: isMobile ? `${theme.spacing.xl} ${theme.spacing.sm}` : `${theme.spacing.massive} ${theme.spacing.lg}` }}>
      <StepIndicator 
        title={editRace ? "Modifier la course" : "Créer une nouvelle course"}
        steps={steps}
        currentStep={currentStep} 
      />
      
      <HeroEditable
          formData={raceInfo}
          handleChange={handleRaceInfoChange}
          onImageChange={handleImageChange}
      />
      <div>
        {/* Step 1: General Information */}
        {currentStep === 1 && (
          <CreateRaceGeneralInfos
            raceInfo={raceInfo}
            setRaceInfo={setRaceInfo}
            extraInfo={extraInfo}
            setExtraInfo={setExtraInfo}
            selectedSportExtra={selectedSportExtra}
            setSelectedSportExtra={setSelectedSportExtra}
            handleRaceInfoChange={handleRaceInfoChange}
            eventId={event_id}
            gpxTrackId={gpxTrackId}
            onGPXTrackIdChange={setGpxTrackId}
          />
        )}

        {/* Step 2: Options & Requirements */}
        {currentStep === 2 && (
          <CreateRaceOptions
            options={options}
            requirements={requirements}
            setOptions={setOptions}
            setRequirements={setRequirements}
          />
        )}

        {/* Step 3: Prices */}
        {currentStep === 3 && (
          <CreateRacePrices
            prices={prices}
            setPrices={setPrices}
            corrals={corrals}
            setCorrals={setCorrals}
          />
        )}

        {error && (
          <div style={styles.errorBox}>
            <p style={styles.errorText}>{error}</p>
          </div>
        )}

        <div style={{ ...styles.stepActions, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          {currentStep > 1 && (
            <Button
              icon={ArrowLeftIcon}
              title="Précédent"
              onClick={() => {
                setCurrentStep(currentStep - 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          )}
          {currentStep < steps.length && (
            <Button
              icon={ArrowRightIcon}
              iconPosition="right"
              title="Suivant"
              onClick={() => {
                setCurrentStep(currentStep + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={currentStep === 1 && !isStep1Valid()}
            />
          )}
          {currentStep === steps.length && (
            <Button
              title={submitting ? (editRace ? "Modification..." : "Création...") : (editRace ? "Modifier la course" : "Créer la course")}
              onClick={handleSubmit}
              disabled={submitting}
            />
          )}
          {editRace && (
            <Button
              title="Annuler"
              onClick={() => navigate(-1)}
              style="cancel"
            />
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  topMargin: {
    backgroundColor: theme.colors.bgLight,
    height: "80px",
    width: "100%",
  },
  container: {
    backgroundColor: theme.colors.bgLight,
    minHeight: "100vh",
  },
  title: {
    margin: `0 0 ${theme.spacing.xxl} 0`,
    fontSize: theme.fonts.size.massive,
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.textInverted,
  },
  mainColumn: {
    padding: `${theme.spacing.massive} ${theme.spacing.lg}`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xxl,
  },
  formRow: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
  },
  label: {
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textMuted,
  },
  input: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.lg,
    fontSize: theme.fonts.size.sm,
  },
  textarea: {
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.lg,
    fontSize: theme.fonts.size.sm,
    fontFamily: "inherit",
    minHeight: "120px",
    resize: "vertical",
  },
  description: {
    margin: `0 0 ${theme.spacing.md} 0`,
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textMuted,
    fontStyle: "italic",
  },
  addButton: {
    width: "100%",
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    marginTop: theme.spacing.md,
    backgroundColor: theme.colors.heroBlue,
    color: theme.colors.white,
    border: "none",
    borderRadius: theme.radius.lg,
    cursor: "pointer",
    fontWeight: theme.fonts.weight.semibold,
    fontSize: theme.fonts.size.sm,
    transition: theme.transitions.base,
  },
  stepActions: {
    display: "flex",
    gap: theme.spacing.md,
    justifyContent: "flex-end",
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.xxl,
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing.md,
    justifyContent: "space-between",
    alignItems: "center",
  },
  errorBox: {
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    backgroundColor: "#fee2e2",
    borderLeft: `4px solid ${theme.colors.error || "#ef4444"}`,
    borderRadius: theme.radius.lg,
    marginBottom: theme.spacing.lg,
  },
  errorText: {
    margin: "0",
    color: "#991b1b",
    fontWeight: theme.fonts.weight.medium,
  },
};
